'use client';

import { useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { register, login, terms as termsRoute, privacy as privacyRoute } from '@/routes';
import AppLogoIcon from '@/components/app-logo-icon';
import { T } from './V2Tokens';
import { PRICING, FAQ_DATA, type PricingPlan } from './V1Data';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

// ─── Pricing ─────────────────────────────────────────────────────────────────

function CheckIcon({ light }: { light?: boolean }) {
    return (
        <svg width={16} height={16} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
            <circle cx={8} cy={8} r={7.5} fill={light ? 'rgba(255,255,255,0.15)' : T.brand + '18'} />
            <path d="M4.5 8l2.5 2.5 4.5-5" stroke={light ? '#fff' : T.brand} strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

export function V2Pricing() {
    const isMobile   = useIsMobile();
    const ref = useRef<HTMLElement>(null);

    const growth     = PRICING[1]; // highlighted, full-width hero
    const launch     = PRICING[0];
    const enterprise = PRICING[2];

    useGSAP(() => {
        if (isMobile) return;
        gsap.fromTo('.v2-price-hero',
            { y: 56, opacity: 0, scale: 0.97 },
            { y: 0, opacity: 1, scale: 1, duration: 0.85, ease: 'power3.out', scrollTrigger: { trigger: ref.current, start: 'top 75%' } }
        );
        gsap.fromTo('.v2-price-flank',
            { y: 40, opacity: 0 },
            { y: 0, opacity: 1, stagger: 0.14, duration: 0.7, ease: 'power3.out', scrollTrigger: { trigger: ref.current, start: 'top 70%' }, delay: 0.18 }
        );
    }, { scope: ref, dependencies: [isMobile] });

    return (
        <section key={isMobile ? 'pricing-m' : 'pricing-d'} ref={ref} id="pricing" style={{ padding: isMobile ? '48px 20px' : '80px 56px', background: T.bg, overflow: 'hidden' }}>
            <div style={{ maxWidth: 1100, margin: '0 auto' }}>

                {/* Header */}
                <div style={{ textAlign: 'center', marginBottom: 56 }} dir="rtl">
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: T.rPill, background: T.bgCard, border: `1px solid ${T.line}`, fontSize: 12, fontWeight: 600, color: T.ink70, marginBottom: 20 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                        الأسعار
                    </span>
                    <h2 style={{ fontSize: 'clamp(34px, 4.2vw, 58px)', fontWeight: 900, lineHeight: 1.08, margin: '0 auto 12px', letterSpacing: '-0.025em', maxWidth: 720, color: T.ink }}>
                        استثمار في نظام نمو — لا في وعود تسويقية.
                    </h2>
                    <p style={{ fontSize: 16, color: T.ink70, margin: 0 }}>بدون عقود طويلة. إلغاء في أي وقت.</p>
                </div>

                {/* ── Growth: full-width hero — horizontal on desktop, stacked on mobile ── */}
                <div
                    className="v2-price-hero"
                    style={{
                        position: 'relative',
                        background: T.brand,
                        borderRadius: isMobile ? 20 : 28,
                        padding: isMobile ? '36px 24px' : '48px 52px',
                        color: '#fff',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: isMobile ? 28 : 0,
                        overflow: 'hidden',
                        marginBottom: 14,
                    }}
                    dir="rtl"
                >
                    {/* decorative glow */}
                    <div aria-hidden style={{ position: 'absolute', top: -100, left: '35%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.14), transparent 60%)', pointerEvents: 'none' }} />

                    {/* Tag */}
                    {growth.tag && (
                        <div style={{ position: 'absolute', top: 20, left: 20, padding: '5px 14px', background: 'rgba(255,255,255,0.18)', color: '#fff', fontSize: 11, fontWeight: 700, borderRadius: 99, letterSpacing: '0.04em' }}>
                            {growth.tag}
                        </div>
                    )}

                    {/* Plan name + price + CTA */}
                    <div style={{ flexShrink: 0, width: isMobile ? '100%' : undefined, minWidth: isMobile ? undefined : 220, maxWidth: isMobile ? '100%' : 270, position: 'relative' }}>
                        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', opacity: 0.55, textTransform: 'uppercase', marginBottom: 6 }}>{growth.name}</div>
                        <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.1 }}>{growth.ar}</div>
                        <div style={{ lineHeight: 0.9, margin: '22px 0 6px' }}>
                            <span style={{ fontSize: isMobile ? 60 : 80, fontWeight: 900, letterSpacing: '-0.04em' }}>{growth.price}</span>
                        </div>
                        <div style={{ fontSize: 13, opacity: 0.52, marginBottom: 28 }}>{growth.unit}</div>
                        <Link
                            href={register.url()}
                            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', padding: '14px 0', borderRadius: 99, background: '#fff', color: T.brand, fontSize: 15, fontWeight: 800, textDecoration: 'none', boxSizing: 'border-box', transition: 'transform .15s, box-shadow .15s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.22)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                        >
                            ابدأ الآن
                        </Link>
                    </div>

                    {/* Divider — hidden on mobile */}
                    {!isMobile && (
                        <div style={{ width: 1, alignSelf: 'stretch', background: 'rgba(255,255,255,0.18)', margin: '0 48px', flexShrink: 0, minHeight: 160 }} />
                    )}

                    {/* Features: 2-column grid on desktop, 1-column on mobile */}
                    <div style={{ flex: 1, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? 10 : '14px 32px', position: 'relative' }}>
                        {growth.feats.map((f, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 15, lineHeight: 1.35 }}>
                                <CheckIcon light />
                                <span style={{ opacity: 0.92 }}>{f}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Launch + Enterprise: compact flankers ── */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14 }}>

                    {/* Launch */}
                    <div className="v2-price-flank" style={{ background: T.bgWhite, border: `1px solid ${T.line}`, borderRadius: 24, padding: isMobile ? '28px 24px' : '36px 40px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} dir="rtl">
                        <div>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: T.ink50, marginBottom: 4 }}>{launch.name}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: T.ink }}>{launch.ar}</div>
                        </div>
                        <div style={{ margin: '20px 0 24px' }}>
                            <span style={{ fontSize: isMobile ? 40 : 54, fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1, color: T.ink }}>{launch.price}</span>
                            <span style={{ fontSize: 13, color: T.ink50, marginRight: 8 }}>{launch.unit}</span>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                            {launch.feats.map((f, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: T.ink70 }}>
                                    <CheckIcon />
                                    {f}
                                </div>
                            ))}
                        </div>
                        <Link
                            href={register.url()}
                            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px 0', borderRadius: 99, background: T.ink, color: '#fff', fontSize: 14, fontWeight: 700, transition: 'opacity .2s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.8'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                        >
                            ابدأ الآن
                        </Link>
                    </div>

                    {/* Enterprise */}
                    <div className="v2-price-flank" style={{ background: T.ink, border: `1px solid transparent`, borderRadius: 24, padding: isMobile ? '28px 24px' : '36px 40px', display: 'flex', flexDirection: 'column', position: 'relative', overflow: 'hidden' }} dir="rtl">
                        <div aria-hidden style={{ position: 'absolute', bottom: -60, left: -60, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(138,124,255,0.22), transparent 65%)', pointerEvents: 'none' }} />
                        <div style={{ position: 'relative' }}>
                            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 4 }}>{enterprise.name}</div>
                            <div style={{ fontSize: 20, fontWeight: 800, color: '#fff' }}>{enterprise.ar}</div>
                        </div>
                        <div style={{ margin: '20px 0 24px', position: 'relative' }}>
                            <span style={{ fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1.1, color: '#fff' }}>{enterprise.price}</span>
                        </div>
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24, position: 'relative' }}>
                            {enterprise.feats.map((f, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: 'rgba(255,255,255,0.72)' }}>
                                    <CheckIcon light />
                                    {f}
                                </div>
                            ))}
                        </div>
                        <a
                            href="tel:+966570903563"
                            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px 0', borderRadius: 99, background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.22)', fontSize: 14, fontWeight: 700, transition: 'border-color .2s', position: 'relative' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.5)'; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.22)'; }}
                        >
                            تواصل معنا
                        </a>
                    </div>
                </div>

            </div>
        </section>
    );
}

// ─── FAQ ─────────────────────────────────────────────────────────────────────

export function V2FAQ() {
    const isMobile = useIsMobile();
    const [open, setOpen] = useState<number>(0);
    const ref = useRef<HTMLElement>(null);

    useGSAP(() => {
        if (isMobile) return;
        gsap.fromTo(
            ref.current?.querySelectorAll('.v2-faq-item') ?? [],
            { y: 20, opacity: 0 },
            {
                y: 0, opacity: 1, stagger: 0.08, duration: 0.5, ease: 'power2.out',
                scrollTrigger: { trigger: ref.current, start: 'top 80%' },
            }
        );
    }, { scope: ref, dependencies: [isMobile] });

    return (
        <section key={isMobile ? 'faq-m' : 'faq-d'} ref={ref} id="faq" style={{ padding: isMobile ? '48px 20px' : '80px 56px', background: T.bgWhite }}>
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
                <div style={{ textAlign: 'center', marginBottom: 56 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', borderRadius: T.rPill, background: T.bgCard, border: `1px solid ${T.line}`, fontSize: 12, fontWeight: 600, color: T.ink70, marginBottom: 24 }}>
                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.brand, flexShrink: 0 }} />
                        أسئلة شائعة
                    </span>
                    <h2 style={{ fontSize: 'clamp(34px, 4vw, 56px)', fontWeight: 900, lineHeight: 1.08, margin: '0', letterSpacing: '-0.025em', color: T.ink }}>
                        قبل ما تبدأ — اقرأ هذا.
                    </h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {FAQ_DATA.map((f, i) => (
                        <div
                            key={i}
                            className="v2-faq-item"
                            style={{
                                background: T.bgCard,
                                border: `1px solid ${open === i ? T.brand300 : T.line}`,
                                borderRadius: 18, padding: '20px 28px', cursor: 'pointer',
                                transition: 'border-color .2s',
                            }}
                            onClick={() => setOpen(open === i ? -1 : i)}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20 }}>
                                <div style={{ fontSize: 17, fontWeight: 700, color: T.ink }}>{f.q}</div>
                                <div style={{
                                    width: 32, height: 32, borderRadius: 99, flexShrink: 0,
                                    background: open === i ? T.ink : 'transparent',
                                    color: open === i ? '#fff' : T.ink,
                                    border: `1px solid ${open === i ? T.ink : T.line}`,
                                    display: 'grid', placeItems: 'center',
                                    transition: 'all .2s', fontSize: 18,
                                }}>
                                    {open === i ? '−' : '+'}
                                </div>
                            </div>
                            {open === i && (
                                <p style={{ fontSize: 15, color: T.ink70, lineHeight: 1.7, marginTop: 14, paddingTop: 14, borderTop: `1px dashed ${T.line}`, marginBottom: 0 }}>
                                    {f.a}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

// ─── Final CTA ────────────────────────────────────────────────────────────────

export function V2FinalCTA() {
    const isMobile = useIsMobile();
    const ref = useRef<HTMLElement>(null);

    useGSAP(() => {
        if (isMobile) return;
        gsap.fromTo(
            ref.current?.querySelectorAll('.v2-cta-el') ?? [],
            { y: 30, opacity: 0 },
            {
                y: 0, opacity: 1, stagger: 0.1, duration: 0.7, ease: 'power3.out',
                scrollTrigger: { trigger: ref.current, start: 'top 75%' },
            }
        );
    }, { scope: ref, dependencies: [isMobile] });

    return (
        <section key={isMobile ? 'cta-m' : 'cta-d'} ref={ref} style={{ padding: isMobile ? '44px 20px' : '80px 56px 80px', background: T.bgWhite }}>
            <div style={{ maxWidth: 900, margin: '0 auto' }}>
                <div id="contact" style={{
                    padding: isMobile ? '44px 24px' : '70px 56px', borderRadius: isMobile ? 24 : 32,
                    background: T.ink, color: '#fff',
                    textAlign: 'center', position: 'relative', overflow: 'hidden',
                }}>
                    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 65% 30%, rgba(138,124,255,0.45), transparent 55%)' }} />
                    <div style={{ position: 'relative' }}>
                        <div className="v2-cta-el" style={{ fontSize: 13, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.5, marginBottom: 20 }}>
                            ابدأ اليوم
                        </div>
                        <h2 className="v2-cta-el" style={{ fontSize: 'clamp(32px, 4vw, 52px)', fontWeight: 900, lineHeight: 1.1, margin: '0 0 16px', letterSpacing: '-0.025em' }}>
                            Growth Engine جاهز.<br />
                            أنت جاهز؟
                        </h2>
                        <p className="v2-cta-el" style={{ fontSize: 17, color: 'rgba(255,255,255,0.65)', maxWidth: 460, margin: '0 auto 36px' }}>
                            7 أيام تجربة مجانية · بدون بطاقة ائتمان · إلغاء في أي وقت
                        </p>
                        <div className="v2-cta-el" style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : undefined }}>
                            <Link
                                href={register.url()}
                                style={{
                                    padding: '16px 30px', borderRadius: 99,
                                    background: T.brand, color: '#fff',
                                    fontSize: 16, fontWeight: 700, textDecoration: 'none',
                                    display: 'inline-flex', alignItems: 'center',
                                    transition: 'transform .15s, box-shadow .15s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 8px 30px rgba(91,79,229,0.45)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                            >
                                ابدأ Growth Engine
                            </Link>
                            <a
                                href="tel:+966570903563"
                                style={{
                                    padding: '16px 30px', borderRadius: 99,
                                    background: 'transparent', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    fontSize: 16, fontWeight: 700, textDecoration: 'none',
                                    display: 'inline-flex', alignItems: 'center',
                                    transition: 'transform .15s, border-color .15s',
                                }}
                                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.4)'; }}
                                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'none'; (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.2)'; }}
                            >
                                احجز جلسة اكتشاف
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

// ─── Footer ───────────────────────────────────────────────────────────────────

export function V2Footer() {
    const isMobile = useIsMobile();
    return (
        <footer style={{ padding: isMobile ? '40px 20px 28px' : '56px 56px 36px', background: T.bg, borderTop: `1px solid ${T.line}` }}>
            <div style={{ maxWidth: 1240, margin: '0 auto' }}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: isMobile ? 32 : 40, marginBottom: isMobile ? 32 : 50 }}>
                    {/* Brand */}
                    <div style={{ maxWidth: 320 }}>
                        <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, color: T.ink, textDecoration: 'none' }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <AppLogoIcon color="white" style={{ width: 18, height: 18 }} />
                            </div>
                            <span style={{ fontSize: 21, fontWeight: 800, letterSpacing: '-0.025em' }}>مجرّد استديو</span>
                        </Link>
                        <p style={{ fontSize: 14, color: T.ink70, lineHeight: 1.65, marginTop: 16 }}>
                            أول وكالة تسويق بالذكاء الاصطناعي تبني Growth Engine متكامل لتحقيق نمو قابل للتوسع.
                        </p>
                    </div>

                    {/* Links */}
                    <div style={{ display: 'flex', gap: isMobile ? 32 : 60, flexWrap: 'wrap' }}>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: T.ink50, textTransform: 'uppercase', marginBottom: 14 }}>المنصة</div>
                            {[['أقسام المنصة', '#divisions'], ['آلية العمل', '#process'], ['الأسعار', '#pricing'], ['أعمالنا', '#portfolio']].map(([l, h]) => (
                                <div key={l} style={{ marginBottom: 10 }}>
                                    <a href={h} style={{ fontSize: 14, color: T.ink70, textDecoration: 'none', transition: 'color .15s' }}
                                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink; }}
                                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink70; }}>
                                        {l}
                                    </a>
                                </div>
                            ))}
                        </div>
                        <div>
                            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', color: T.ink50, textTransform: 'uppercase', marginBottom: 14 }}>قانوني</div>
                            <div style={{ marginBottom: 10 }}>
                                <Link href={termsRoute.url()} style={{ fontSize: 14, color: T.ink70, textDecoration: 'none', transition: 'color .15s' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink70; }}>
                                    الشروط والأحكام
                                </Link>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                                <Link href={privacyRoute.url()} style={{ fontSize: 14, color: T.ink70, textDecoration: 'none', transition: 'color .15s' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink70; }}>
                                    سياسة الخصوصية
                                </Link>
                            </div>
                            <div>
                                <a href="tel:+966570903563" style={{ fontSize: 14, color: T.ink70, textDecoration: 'none', direction: 'ltr', display: 'inline-block', transition: 'color .15s' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink70; }}>
                                    +966 570 903 563
                                </a>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ paddingTop: 24, borderTop: `1px solid ${T.line}`, display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ fontSize: 13, color: T.ink50 }}>© {new Date().getFullYear()} مجرّد استديو · جميع الحقوق محفوظة</div>
                    <div style={{ display: 'flex', gap: 20, fontSize: 13, color: T.ink50 }}>
                        <Link href={privacyRoute.url()} style={{ color: 'inherit', textDecoration: 'none' }}>سياسة الخصوصية</Link>
                        <Link href={termsRoute.url()} style={{ color: 'inherit', textDecoration: 'none' }}>الشروط والأحكام</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
}
